import React, { useEffect, useState, useRef } from "react";
import { Card } from "react-bootstrap";
import { Chart } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import { FaClipboardList, FaCalendarAlt, FaTimes, FaChevronLeft, FaChevronRight, FaChevronDown } from "react-icons/fa";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend } from "chart.js";

import { fetchTicketsByStatusWithPriority } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const CalendarRangePicker = ({ onDateSelect, onClose }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 90);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dayNames = ["M", "T", "W", "T", "F", "S", "S"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const days = [];
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonth.getDate() - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonth.getDate() - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 35 - days.length;
    for (let i = 1; i <= Math.max(0, remaining); i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days.slice(0, 35);
  };

  const days = getDaysInMonth(currentMonth);
  const isDateInRange = (date) => date >= minDate && date <= today;
  const isSelected = (date) => {
    if (!selectedStart && !selectedEnd) return false;
    if (selectedStart && date.getTime() === selectedStart.getTime()) return true;
    if (selectedEnd && date.getTime() === selectedEnd.getTime()) return true;
    if (selectedStart && selectedEnd) return date >= selectedStart && date <= selectedEnd;
    return false;
  };
  const isInRange = (date) => {
    if (!selectedStart || !selectedEnd) return false;
    return date > selectedStart && date < selectedEnd;
  };

  const handleDateClick = (date) => {
    if (!isDateInRange(date)) return;
    if (selectingStart || (!selectedStart && !selectedEnd)) {
      setSelectedStart(date);
      setSelectedEnd(null);
      setSelectingStart(false);
    } else {
      if (date < selectedStart) {
        setSelectedStart(date);
        setSelectedEnd(selectedStart);
      } else {
        setSelectedEnd(date);
      }
    }
  };

  const handleApply = () => {
    if (selectedStart && selectedEnd) {
      onDateSelect(selectedStart, selectedEnd);
    } else if (selectedStart) {
      onDateSelect(selectedStart, selectedStart);
    }
  };

  const isDarkMode = document.body.classList.contains('dark-mode');
  const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
  const prevMonthEnd = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
  const canGoPrev = prevMonthEnd >= minDate;
  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
  const nextMonthStart = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
  const canGoNext = nextMonthStart <= today;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -5, scale: 0.98 }}
      transition={{ duration: 0.12 }}
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        zIndex: 1000,
        background: isDarkMode ? "#1e2139" : "#fff",
        borderRadius: 12,
        boxShadow: isDarkMode ? "0 8px 30px rgba(0,0,0,0.4)" : "0 8px 30px rgba(0,0,0,0.12)",
        padding: "10px",
        width: 240,
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: isDarkMode ? "#a0a4b8" : "#6b7280", marginBottom: 8, paddingBottom: 6, borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6" }}>
        Select Date Range
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <button onClick={() => canGoPrev && setCurrentMonth(prevMonth)} disabled={!canGoPrev} style={{ background: "none", border: "none", cursor: canGoPrev ? "pointer" : "not-allowed", padding: 3, borderRadius: 4, color: canGoPrev ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), display: "flex", alignItems: "center", opacity: canGoPrev ? 1 : 0.3 }}>
          <FaChevronLeft size={9} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 11, color: isDarkMode ? "#e2e4e9" : "#1f2937" }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
        <button onClick={() => canGoNext && setCurrentMonth(nextMonth)} disabled={!canGoNext} style={{ background: "none", border: "none", cursor: canGoNext ? "pointer" : "not-allowed", padding: 3, borderRadius: 4, color: canGoNext ? (isDarkMode ? "#a0a4b8" : "#6b7280") : (isDarkMode ? "#4a4f63" : "#d1d5db"), display: "flex", alignItems: "center", opacity: canGoNext ? 1 : 0.3 }}>
          <FaChevronRight size={9} />
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 3 }}>
        {dayNames.map((day, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 8, fontWeight: 600, color: isDarkMode ? "#6b7280" : "#9ca3af", padding: "1px 0" }}>{day}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {days.map((dayObj, i) => {
          const dateInRange = isDateInRange(dayObj.date);
          const selected = isSelected(dayObj.date);
          const inRange = isInRange(dayObj.date);
          const disabled = !dayObj.isCurrentMonth || !dateInRange;
          return (
            <button
              key={i}
              onClick={() => handleDateClick(dayObj.date)}
              disabled={disabled}
              style={{
                width: 26, height: 26, borderRadius: 5, border: selected ? "2px solid #8b5cf6" : "1px solid transparent",
                background: selected ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : inRange ? (isDarkMode ? "rgba(139, 92, 246, 0.15)" : "rgba(139, 92, 246, 0.1)") : "transparent",
                color: selected ? "#fff" : disabled ? (isDarkMode ? "#4a4f63" : "#d1d5db") : (isDarkMode ? "#e2e4e9" : "#374151"),
                fontSize: 10, fontWeight: selected ? 600 : 400, cursor: disabled ? "default" : "pointer", transition: "all 0.12s ease",
                display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box", padding: 0, margin: 0, lineHeight: 1, textAlign: "center",
              }}
            >
              {dayObj.day}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, marginTop: 10, paddingTop: 10, borderTop: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6" }}>
        <button onClick={onClose} style={{ padding: "5px 10px", borderRadius: 5, border: "none", background: "transparent", color: isDarkMode ? "#a0a4b8" : "#6b7280", fontSize: 10, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
        <button onClick={handleApply} disabled={!selectedStart || !selectedEnd} style={{ padding: "5px 10px", borderRadius: 5, border: "none", background: (selectedStart && selectedEnd) ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" : (isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"), color: (selectedStart && selectedEnd) ? "#fff" : (isDarkMode ? "#6b7280" : "#9ca3af"), fontSize: 10, fontWeight: 600, cursor: (selectedStart && selectedEnd) ? "pointer" : "not-allowed", opacity: (selectedStart && selectedEnd) ? 1 : 0.5 }}>Apply</button>
      </div>
    </motion.div>
  );
};

const TicketStatusChart = ({ className, clientId }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return { start, end };
  };
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const isInitialLoad = useRef(true);

  const getAxisTextColor = () => {
    const isDark = document.body.classList.contains('dark-mode');
    return isDark ? '#a0a4b8' : '#6c737f';
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  const formatDateRangeForDisplay = (start, end) => {
    if (!start || !end) return "";
    return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`;
  };

  const formatDateForApi = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const clearDateRange = (e) => {
    if (e) e.stopPropagation();
    setDateRange(getDefaultDateRange());
    setShowCalendar(false);
  };

  const handleDateRangeSelect = (start, end) => {
    setDateRange({ start, end });
    setShowCalendar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!clientId) return;

    const startApi = formatDateForApi(dateRange.start);
    const endApi = formatDateForApi(dateRange.end);
    const cacheKey = `tickets_by_status_${startApi}_${endApi}`;
    const cached = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      setStatusData(cached);
      setLoading(false);
      setIsRefetching(false);
      isInitialLoad.current = false;
      return;
    }

    if (isInitialLoad.current) {
      setLoading(true);
    } else {
      setIsRefetching(true);
    }

    fetchTicketsByStatusWithPriority(clientId, startApi, endApi)
      .then((res) => {
        setStatusData(res);
        setCachedData(cacheKey, res, clientId);
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      })
      .catch(() => {
        setStatusData({ labels: [], high_priority: [], normal_priority: [] });
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      });
  }, [clientId, dateRange, getCachedData, setCachedData]);

  if (loading || !statusData) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={className}>
        <Card
          className="shadow-sm"
          style={{
            borderRadius: 16,
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
          }}
        >
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
              <h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: "rgba(99,102,241,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FaClipboardList size={16} color="#6366f1" />
                </div>
                Open Tickets by Status
              </h6>
            </div>
            <div style={{ minHeight: "47dvh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 48 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ height: [14, 44, 14] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.12 }}
                    style={{
                      width: 10,
                      background: "rgba(99,102,241,0.35)",
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                ))}
              </div>
              <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading...</span>
            </div>
          </Card.Body>
        </Card>
      </motion.div>
    );
  }

  const rawLabels = statusData.labels || [];
  const rawHighPriority = statusData.high_priority || [];
  const rawNormalPriority = statusData.normal_priority || [];

  // Sort labels alphabetically, keeping data arrays in sync
  const sortedIndices = rawLabels
    .map((label, i) => ({ label, i }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(({ i }) => i);

  const labels = sortedIndices.map(i => rawLabels[i]);
  const highPriorityData = sortedIndices.map(i => rawHighPriority[i] ?? 0);
  const normalPriorityData = sortedIndices.map(i => rawNormalPriority[i] ?? 0);


  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "High Priority",
        data: highPriorityData,
        backgroundColor: "#ef4444",
        // Round top of red only when there is no blue segment on top
        borderRadius: highPriorityData.map((v, i) =>
          (normalPriorityData[i] || 0) > 0 || (v || 0) === 0
            ? 0
            : { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
        ),
        borderSkipped: "bottom",
        stack: "tickets",
        order: 2,
      },
      {
        type: "bar",
        label: "Normal Priority",
        data: normalPriorityData,
        backgroundColor: "#3b82f6",
        // Round top of blue only when it has a value (it's always on top)
        borderRadius: normalPriorityData.map((v) =>
          (v || 0) > 0
            ? { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
            : 0
        ),
        borderSkipped: "bottom",
        stack: "tickets",
        order: 2,
      },
    ],
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={className}>
      <Card
        className="shadow-sm"
        style={{
          borderRadius: 16,
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-border)",
        }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
            <h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: "rgba(99,102,241,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FaClipboardList size={16} color="#6366f1" />
              </div>
              Open Tickets by Status
            </h6>

            <div className="position-relative" ref={calendarRef}>
              <div
                onClick={() => setShowCalendar(!showCalendar)}
                style={{
                  borderRadius: 8,
                  background: "var(--color-bg-card)",
                  border: dateRange && dateRange.start && dateRange.end ? "1px solid rgba(139, 92, 246, 0.4)" : "1px solid var(--color-border)",
                  padding: "4px 8px",
                  fontSize: 11,
                  cursor: "pointer",
                  maxWidth: 240,
                  minWidth: 140,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = (dateRange && dateRange.start && dateRange.end) ? "rgba(139, 92, 246, 0.4)" : "var(--color-border)"; }}
              >
                <FaCalendarAlt size={10} color="#8b5cf6" style={{ flexShrink: 0 }} />
                <span style={{ color: (dateRange && dateRange.start && dateRange.end) ? "#a78bfa" : "var(--color-text)", fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {dateRange && dateRange.start && dateRange.end ? formatDateRangeForDisplay(dateRange.start, dateRange.end) : "Date Range"}
                </span>
                {dateRange && dateRange.start && dateRange.end ? (
                  <div
                    onClick={(e) => { e.stopPropagation(); clearDateRange(e); }}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "rgba(239, 68, 68, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.25)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.15)"; }}
                  >
                    <FaTimes size={8} color="#dc2626" />
                  </div>
                ) : (
                  <FaChevronDown size={9} style={{ color: "var(--color-text-light)", transform: showCalendar ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }} />
                )}
              </div>
              <AnimatePresence>{showCalendar && <CalendarRangePicker onDateSelect={handleDateRangeSelect} onClose={() => setShowCalendar(false)} />}</AnimatePresence>
            </div>
          </div>

          <div style={{ minHeight: "47dvh", position: "relative" }}>
            {/* Loading Overlay */}
            <AnimatePresence>
              {isRefetching && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 48 }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ height: [14, 44, 14] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.12 }}
                        style={{
                          width: 10,
                          background: "rgba(99,102,241,0.35)",
                          borderRadius: "4px 4px 0 0",
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading...</span>
                </motion.div>
              )}
            </AnimatePresence>
            {isRefetching ? null : labels.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: "47dvh",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <FaClipboardList size={28} style={{ color: "var(--color-text-light)", opacity: 0.35 }} />
                <span style={{ color: "var(--color-text-light)", fontSize: 14 }}>
                  No ticket data available
                </span>
              </div>
            ) : (
              <Chart
                type="bar"
                data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },

                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      color: (() => {
                        const isDark = document.body.classList.contains('dark-mode');
                        return isDark ? '#a0a4b8' : '#6c737f';
                      })(),
                      usePointStyle: true,
                      padding: 15,
                    },
                  },
                  tooltip: {
                    backgroundColor: "#000000",
                    titleColor: "#ffffff",
                    bodyColor: "#ffffff",
                    borderColor: "#000000",
                    borderWidth: 0,
                    padding: 10,
                    displayColors: true,
                  },
                },

                scales: {
                  x: {
                    stacked: true,
                    ticks: {
                      color: getAxisTextColor(),
                      maxRotation: 45,
                      minRotation: 0,
                    },
                    grid: { display: false },
                  },
                  y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: {
                      color: getAxisTextColor(),
                      stepSize: 1,
                      callback: function(value) {
                        return Number.isInteger(value) ? value : null;
                      }
                    },
                    grid: {
                      display: false,
                    },
                  },
                },
              }}
              />
            )}
          </div>
        </Card.Body>
      </Card>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
};

export default TicketStatusChart;
